import React, { useState, useCallback, useEffect } from "react";

import referenceSourceApi from "apis/reference_sources";
import {
  Button,
  SubHeader,
  PageLoader,
  Typography,
} from "clearsense-ui/components";
import { Plus } from "components/Common/clearsense-ui/icons";
import ContentBox from "components/Common/ContentBox";
import FilterHeader from "components/Common/FilterHeader";
import PermissibleWrapper from "components/Common/PermissibleWrapper";
import QuickFilter from "components/Common/QuickFilter";
import SummaryBarChart from "components/Common/SummaryBarChart";
import { useColumnHeaderSelector } from "hooks/useColumnHeaderSelector";
import useSearchAndPaginate from "hooks/useSearchAndPaginate";

import DeleteReferenceSource from "./DeleteReferenceSource";
import NewReferenceSource from "./NewReferenceSource";
import ReferenceSourcesTable from "./Table";

import { REFERENCE_SOURCE_COLUMNS } from "../constants/referenceSourceTable";

const ReferenceSourceHome = ({ userPermissions }) => {
  const [isNewRefDataOpen, setIsNewRefDataOpen] = useState(false);
  const [checkedRows, setCheckedRows] = useState([]);
  const [rowToBeDeleted, setRowToBeDeleted] = useState({});
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [referenceSourcesData, setReferenceSourcesData] = useState([]);
  const [referenceSourceCount, setReferenceSourceCount] = useState(0);
  const [summaryData, setSummaryData] = useState([]);
  const [isGraphLoading, setIsGraphLoading] = useState(true);
  const [sort, setSort] = useState("top");
  const [filter, setFilter] = useState({
    filterBy: "",
    filterValue: "",
  });
  const quickFilterData = [
    {
      label: "Missing Description",
      value: referenceSourcesData?.missing_description_count,
      onClick: () =>
        setFilter({
          filterBy: "description =",
          filterValue: "",
        }),
    },
    {
      label: "Missing Crosswalk",
      value: referenceSourcesData?.missing_crosswalk_count,
      onClick: () =>
        setFilter({
          filterBy: "missing_crosswalk",
          filterValue: "",
        }),
    },
  ];

  const findPercentage = (numerator, totalCount) => {
    const denominator = totalCount === 0 ? 1 : totalCount;
    return (numerator / denominator) * 100;
  };

  const fetchReferenceSourceSummary = async () => {
    try {
      setIsGraphLoading(true);
      const { data } = await referenceSourceApi.fetchReferenceSourceSummary(
        sort
      );
      setSummaryData(
        data?.summary_data.map(object => ({
          name: object.name,
          Enterprise: findPercentage(object.enterprise, object.total_count),
          "Non-Enterprise": findPercentage(
            object.total_count - object.enterprise,
            object.total_count
          ),
        }))
      );
    } catch (error) {
      logger.log(error);
    } finally {
      setIsGraphLoading(false);
    }
  };

  const handleDelete = record => {
    setRowToBeDeleted(record);
    setIsDeleteModalOpen(true);
  };

  const fetchReferenceSourcesData = useCallback(async () => {
    try {
      setIsTableLoading(true);
      const params = new URLSearchParams(window.location.search);
      const search = params.get("search") || "";
      const currentPage = params.get("page") || 1;
      const pageSize = params.get("per_page") || 10;
      const { data } = await referenceSourceApi.index(
        search.toLowerCase(),
        currentPage,
        pageSize,
        filter.filterBy,
        filter.filterValue
      );
      setReferenceSourcesData(data);
      setReferenceSourceCount(data?.reference_source_count);
    } catch (error) {
      logger.log(error);
    } finally {
      setIsTableLoading(false);
    }
  }, [filter]);

  const filterParams = useSearchAndPaginate(fetchReferenceSourcesData, {
    page: () => setCheckedRows([]),
    pageSize: () => setCheckedRows([]),
  });

  const columnHeaderSelector = useColumnHeaderSelector(
    REFERENCE_SOURCE_COLUMNS(handleDelete, userPermissions)
  );

  useEffect(() => {
    fetchReferenceSourceSummary();
  }, [sort]);

  return (
    <div className="cs-ui-container cs-ui-container__table">
      <SubHeader
        className="mt-8"
        breadcrumbs={[
          { label: "Reference Source", link: "#" },
          { label: "Overview", link: "#" },
        ]}
        actionBlock={
          <PermissibleWrapper
            currentPermissions={userPermissions}
            acceptablePermissions="create"
          >
            <Button
              label="Create Reference Source"
              icon={Plus}
              onClick={() => {
                setIsNewRefDataOpen(true);
              }}
              data-cy="new-reference-source-button"
            />
          </PermissibleWrapper>
        }
      />
      <ContentBox className="flex flex-col justify-center space-y-4 mt-4 items-center bg-white dark:bg-[#2f3941] w-full dark:text-white">
        <Typography style="h3" type="semibold" className="justify-center flex">
          Summary
        </Typography>
        <div className="flex space-x-4 w-full">
          <QuickFilter
            className="w-1/2"
            quickFilterData={quickFilterData}
            disabled={isTableLoading}
          />
          <SummaryBarChart
            topFiveAction={() => setSort("top")}
            bottomFiveAction={() => setSort("bottom")}
            className="w-1/2"
            data={summaryData}
            isLoading={isGraphLoading}
            title="Enterprise Sources by Domain"
            isValuePercent
          />
        </div>
      </ContentBox>
      <FilterHeader
        entity="Reference Source"
        filterParams={filterParams}
        columnHeaderSelector={columnHeaderSelector}
        count={referenceSourceCount}
        checkedRows={checkedRows}
        setIsDeleteAlertOpen={setIsDeleteModalOpen}
        userPermissions={userPermissions}
      />
      {isTableLoading ? (
        <PageLoader />
      ) : (
        <ReferenceSourcesTable
          referenceSourceCount={referenceSourceCount}
          setIsNewRefDataOpen={setIsNewRefDataOpen}
          userPermissions={userPermissions}
          columns={columnHeaderSelector.columns}
          referenceSourcesData={referenceSourcesData?.reference_sources}
          setCheckedRows={setCheckedRows}
          checkedRows={checkedRows}
          filterParams={filterParams}
        />
      )}
      <DeleteReferenceSource
        checkedRows={checkedRows}
        rowToBeDeleted={rowToBeDeleted}
        setRowToBeDeleted={setRowToBeDeleted}
        isDeleteModalOpen={isDeleteModalOpen}
        setIsDeleteModalOpen={setIsDeleteModalOpen}
        fetchReferenceSourcesData={fetchReferenceSourcesData}
        setCheckedRows={setCheckedRows}
      />
      <NewReferenceSource
        isPaneOpen={isNewRefDataOpen}
        setIsPaneOpen={setIsNewRefDataOpen}
        fetchReferenceSourcesData={fetchReferenceSourcesData}
      />
    </div>
  );
};

export default ReferenceSourceHome;
